import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ActionReducer } from '@ngrx/store/src/models';
import {
  APPLICATION_FILTERS_ACTIONS,
  APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES,
  APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES,
} from '../actions/filters.actions';
import { EPeriodOptions, IMonthVsMonth, IPeriod } from '../../../shared/interfaces/time-periods.interface';
import { EDataLoadingMode } from '../../../shared/constants/filter-data-loading-mode.config';

export const FILTERS_STATE_KEY = 'app-filters';

export interface IApplicationFiltersState {
  accountId: number | null;
  siteId: number | null;
  siteIds: number[];
  accountIds: number[];
  scorecardIds: number[];
  validated: boolean;
  period: IPeriod;
  monthVsMonth: IMonthVsMonth;
  dataLoadingMode: EDataLoadingMode;
  remoteFilterData: {
    loading: boolean;
    error: boolean;
    creating: boolean;
  };
}

const initialState: IApplicationFiltersState = {
  validated: false,
  accountId: null,
  siteId: null,
  siteIds: [],
  accountIds: [],
  scorecardIds: [],
  period: {
    name: EPeriodOptions.today,
    displayName: 'Today',
  },
  dataLoadingMode: EDataLoadingMode.AUTO,
  monthVsMonth: {
    firstMonth: null,
    secondMonth: null,
  },
  remoteFilterData: {
    loading: false,
    error: false,
    creating: false,
  },
};

const reducer: ActionReducer<IApplicationFiltersState> = createReducer<IApplicationFiltersState>(
  initialState,
  on(APPLICATION_FILTERS_ACTIONS.validated, (state, { data }) => ({
    ...state,
    ...data,
    validated: true,
  })),
  on(APPLICATION_FILTERS_ACTIONS.update, (state, { data }) => ({
    ...state,
    ...data,
  })),
  on(APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.get, (state) => ({
    ...state,
    remoteFilterData: { loading: true, error: false, creating: false },
  })),
  on(APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.success, (state) => ({
    ...state,
    remoteFilterData: { loading: false, error: false, creating: false },
  })),
  on(APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.error, (state) => ({
    ...state,
    remoteFilterData: { loading: false, error: true, creating: false },
  })),
  on(APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.save, (state) => ({
    ...state,
    remoteFilterData: { ...state.remoteFilterData, creating: true },
  })),
  on(
    APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.success,
    APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.error,
    (state) => ({
      ...state,
      remoteFilterData: { ...state.remoteFilterData, creating: false },
    }),
  ),
  on(APPLICATION_FILTERS_ACTIONS.setLoadingMode, (state, { mode }) => ({
    ...state,
    dataLoadingMode: mode,
  })),
  on(APPLICATION_FILTERS_ACTIONS.setPeriod, (state, { period }) => ({ ...state, period })),
  on(APPLICATION_FILTERS_ACTIONS.setMonthVSMonth, (state, { firstMonth, secondMonth }) => ({
    ...state,
    monthVsMonth: { firstMonth, secondMonth },
  })),
  on(APPLICATION_FILTERS_ACTIONS.setScorecardIds, (state, { ids }) => ({
    ...state,
    scorecardIds: ids,
  })),
);

export const APPLICATION_FILTERS_REDUCER = createFeature({
  name: FILTERS_STATE_KEY,
  reducer,
  extraSelectors: ({ 'selectApp-filtersState': state }) => ({
    isStateReady: createSelector(state, ({ validated, remoteFilterData: { loading } }) => validated && !loading),
    isValidated: createSelector(state, ({ validated }) => validated),
  }),
});
